import { z } from 'zod';

/** Normaliza emails mal guardados en backend (p. ej. coma antes de @) y dominios de prueba conocidos. */
function normalizarEmailApi(val: unknown): unknown {
  if (typeof val !== 'string') {
    return val;
  }
  // 1. Normalizar coma antes de @
  let email = val.replace(/,@/g, '@').trim();

  // 2. Normalizar dominios de prueba conocidos (ej: -TEST en el TLD)
  //    Ejemplo: tide.cl-TEST -> tide.cl
  const atIndex = email.lastIndexOf('@');
  if (atIndex > 0 && atIndex < email.length - 1) {
    const domain = email.substring(atIndex + 1);
    if (domain.endsWith('-TEST')) {
      const newDomain = domain.substring(0, domain.length - 5); // remove '-TEST'
      email = email.substring(0, atIndex + 1) + newDomain;
    }
  }

  return email;
}

/**
 * Esquema Zod para permisos de visitas (respuesta de /me por perfil).
 * "crear" se unificó con "agendar". Claves faltantes o undefined se interpretan como false.
 */
export const PermisosVisitasSchema = z.object({
  agendar: z.boolean().optional().default(false),
  cancelar: z.boolean().optional().default(false),
  eliminar: z.boolean().optional().default(false),
  registrar: z.boolean().optional().default(false),
  reagendar: z.boolean().optional().default(false),
  ver_detalles: z.boolean().optional().default(false)
});

/**
 * Esquema Zod para el nodo permisos de un perfil
 */
export const PermisosPerfilSchema = z.object({
  visitas: PermisosVisitasSchema.optional().default({
    agendar: false,
    cancelar: false,
    eliminar: false,
    registrar: false,
    reagendar: false,
    ver_detalles: false
  })
});

/**
 * Esquema Zod para validar un Perfil de usuario
 */
export const PerfilSchema = z.object({
  id_perfil: z.number().int().positive(),
  descripcion: z.string().min(1),
  permisos: PermisosPerfilSchema.optional()
});

/**
 * Esquema Zod para validar un Usuario
 * 
 * Valida la estructura de datos del usuario recibida de la API de autenticación
 */
export const UsuarioSchema = z.object({
  sub: z.string().min(1, 'El sub (subject) es requerido'),
  username: z.string().email('El username debe ser un email válido').or(z.string().min(1)),
  nombre: z.string().min(1, 'El nombre es requerido'),
  email: z.preprocess(normalizarEmailApi, z.string().email('El email debe ser válido')),
  rut: z.string().min(1, 'El RUT es requerido'),
  tipo_acceso: z.number().int(),
  perfiles: z.array(PerfilSchema).default([]),
  exp: z.number().int().positive().optional(),
  acepta_terminos: z.boolean().optional().default(false)
});

/**
 * Esquema para la respuesta de login
 */
export const LoginResponseSchema = z.object({
  access_token: z.string().min(1, 'El access_token es requerido'),
  refresh_token: z.string().min(1, 'El refresh_token es requerido').optional()
});

/**
 * Esquema para la respuesta de /me
 */
export const UserMeResponseSchema = UsuarioSchema;

/**
 * Tipos TypeScript inferidos
 */
export type PermisosVisitasValidated = z.infer<typeof PermisosVisitasSchema>;
export type PermisosPerfilValidated = z.infer<typeof PermisosPerfilSchema>;
export type UsuarioValidated = z.infer<typeof UsuarioSchema>;
export type PerfilValidated = z.infer<typeof PerfilSchema>;
export type LoginResponseValidated = z.infer<typeof LoginResponseSchema>;